<template>
  <div
    class="ref-nums-section-row flex flex-gap-1 justify-content-between align-items-center"
  >
    <PopupReferenceNumberValueEdit
      :id="id"
      ref="input"
      :key="internalValue.typeId"
      v-model="internalValue"
      :editable="editable"
      :client-types="clientTypes"
      :disabled="!editable"
      :validation-rules="rules"
      class="w-10"
      @delete="emit('delete', internalValue)"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
    />

    <div class="ref-nums-section-row-actions w-2 align-items-center">
      <FontAwesomeIcon
        v-if="editable"
        class="edit-icon"
        aria-hidden="true"
        :class="{ 'no-label': !label, visible: isHovering }"
        :icon="['fas', 'pencil']"
        @click="onFocus"
      />
      <RygenCopyButton
        mode="tertiary"
        :aria-label="
          t('common.copyParamToClipboard', {
            param: t('common.paramValues', {
              param: t('referenceData.referenceNumbers'),
            }),
          })
        "
        :copy-value="internalValue.value ?? ''"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, nextTick, ref, toRefs, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useModelValue } from '@rygen/rygen-ui/composables';
import { capitalize } from '@rygen/rygen-ui/i18n';
import { ClientReferenceNumberType } from '@rygen/rygen-ui/model';

import { ReferenceNumber } from '@/model';

import PopupReferenceNumberValueEdit from '@/components/referenceNumber/PopupReferenceNumberValueEdit.vue';
import RygenCopyButton from '@rygen/rygen-ui/components/button/RygenCopyButton.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: ReferenceNumber,
    default: () => new ReferenceNumber(),
  },
  clientTypes: {
    type: Array as PropType<ClientReferenceNumberType[]>,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, clientTypes, editable } = toRefs(props);
const emit = defineEmits<{
  (event: 'delete', refNum: ReferenceNumber): void;
  (event: 'update:model-value', newVal: ReferenceNumber): void;
}>();

const input = useTemplateRef('input');

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<ReferenceNumber>) => new ReferenceNumber(opts),
});

const isInEditMode = ref(false);
const isHovering = ref(false);

const label = computed(() => {
  const name = internalValue.value?.typeName;
  if (name) {
    return capitalize(name);
  } else {
    return t('common.paramValues', {
      param: t('referenceData.referenceNumbers'),
    });
  }
});

const rules = {
  value: {
    required: required(label.value),
  },
};

const onFocus = () => {
  if (!editable.value) {
    return;
  }

  isInEditMode.value = true;
  nextTick(() => input.value?.$el?.focus());
};
</script>

<style>
.ref-nums-section-row {
  .edit-icon {
    transition: opacity 100ms ease-in;
    opacity: 0;

    &.visible {
      opacity: 1;
    }
  }
}
</style>
